import React, { useEffect, useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { api } from "@/lib/api";
import { KpiGrid, StatusChip } from "@/components/kcc/Kpi";
import { Panel, ModuleHeader } from "@/components/kcc/Panel";
import { DataTable, statusToChip } from "@/components/kcc/DataTable";
import { Play, Pause, Loader2, Radio, Sparkles, ChevronRight, ChevronLeft, Maximize2, X, ArrowRight, Film, Clapperboard, Brain, FileSignature, MessagesSquare, Send, ShieldCheck } from "lucide-react";

/* ============================================================
 * Executive Storyline Engine
 * Guided operational storylines that illuminate every workspace.
 * ==========================================================*/
export function StorylineEngine() {
    const nav = useNavigate();
    const [items, setItems] = useState([]);
    const [selected, setSelected] = useState(null);
    const [detail, setDetail] = useState(null);
    const [playing, setPlaying] = useState(false);
    const [step, setStep] = useState(0);
    const timerRef = useRef(null);
    // Executive AI Situation Room state
    const [narrations, setNarrations] = useState({}); // { [step_seq]: narration text }
    const [narrating, setNarrating] = useState(false);
    const [sessionId, setSessionId] = useState(null);
    const [report, setReport] = useState(null);
    const [reportBusy, setReportBusy] = useState(false);
    const [reportErr, setReportErr] = useState(null);
    const [askQ, setAskQ] = useState("");
    const [askA, setAskA] = useState(null);
    const [askBusy, setAskBusy] = useState(false);

    // Load storyline library once on mount.
    // eslint-disable-next-line react-hooks/exhaustive-deps
    useEffect(() => { api.get("/storylines").then(({ data }) => setItems(data.storylines || [])); }, []);

    async function open(s) {
        setSelected(s);
        setDetail(null);
        setStep(0);
        setPlaying(false);
        setNarrations({});
        setReport(null);
        setReportErr(null);
        setAskA(null);
        setSessionId(null);
        const { data } = await api.get(`/storylines/${s.id}`);
        setDetail(data);
        try {
            await api.post("/storylines/execute", { storyline_id: s.id });
        } catch (err) {
            console.error("Failed to record storyline execution:", err);
        }
    }

    // Auto-narrate the current step whenever it changes AND we have detail loaded.
    useEffect(() => {
        if (!detail || !detail.steps?.length) return;
        const stepDoc = detail.steps[step];
        if (!stepDoc) return;
        if (narrations[stepDoc.seq]) return; // cached
        let cancelled = false;
        setNarrating(true);
        api.post("/situation/narrate", { storyline_id: detail.id, step_seq: stepDoc.seq, session_id: sessionId })
            .then(({ data }) => {
                if (cancelled) return;
                setNarrations((n) => ({ ...n, [stepDoc.seq]: data.narration }));
                if (data.session_id) setSessionId(data.session_id);
            })
            .catch((err) => {
                if (cancelled) return;
                setNarrations((n) => ({ ...n, [stepDoc.seq]: `AI narration failed: ${err?.response?.data?.detail || err.message}` }));
            })
            .finally(() => { if (!cancelled) setNarrating(false); });
        return () => { cancelled = true; };
    }, [detail, step, sessionId, narrations]);

    async function generateReport() {
        if (!detail) return;
        setReportBusy(true);
        setReportErr(null);
        try {
            const { data } = await api.post("/situation/incident-report", { storyline_id: detail.id, session_id: sessionId, log_to_decision_register: true });
            setReport(data);
        } catch (err) {
            setReportErr(err?.response?.data?.detail || err.message);
        } finally {
            setReportBusy(false);
        }
    }

    async function submitAsk() {
        if (!askQ.trim() || !detail) return;
        setAskBusy(true);
        try {
            const { data } = await api.post("/situation/ask", { storyline_id: detail.id, question: askQ, session_id: sessionId });
            setAskA(data.reply);
            if (data.session_id) setSessionId(data.session_id);
        } catch (err) {
            setAskA(`AI failed: ${err?.response?.data?.detail || err.message}`);
        } finally {
            setAskBusy(false);
        }
    }

    useEffect(() => {
        clearInterval(timerRef.current);
        if (playing && detail) {
            timerRef.current = setInterval(() => {
                setStep((s) => {
                    if (s >= detail.steps.length - 1) { setPlaying(false); return s; }
                    return s + 1;
                });
            }, 2500);
        }
        return () => clearInterval(timerRef.current);
    }, [playing, detail]);

    function stepBack() { setStep((s) => Math.max(0, s - 1)); }
    function stepFwd() { setStep((s) => Math.min((detail?.steps.length || 1) - 1, s + 1)); }

    const kpis = useMemo(() => {
        if (!items.length) return [];
        const totalImpact = items.reduce((s, i) => s + (i.impact_musd || 0), 0).toFixed(1);
        const p1 = items.filter((i) => i.severity === "P1").length;
        return [
            { key: "count", label: "Available Storylines", value: String(items.length), status: "normal" },
            { key: "p1", label: "P1 Scenarios", value: String(p1), status: "warning" },
            { key: "impact", label: "Illustrative Impact", value: `$${totalImpact}M`, status: "normal" },
            { key: "coverage", label: "Workspace Coverage", value: "18 of 18", status: "normal" },
        ];
    }, [items]);

    return (
        <div className="space-y-6">
            <ModuleHeader
                kicker="Executive Storyline Engine"
                title="Feel KCC360 respond to real operational events"
                description="Guided scenarios that illuminate every workspace in sequence — production, maintenance, safety, finance, ESG, community and governance. Every step routes to a real KCC360 screen." />
            <KpiGrid kpis={kpis} accent="purple" cols={4} />
            <div className="grid grid-cols-1 xl:grid-cols-3 gap-6">
                <Panel title="Storyline Library" accent="copper">
                    <div className="space-y-2" data-testid="storyline-library">
                        {items.map((s) => (
                            <button key={s.id} onClick={() => open(s)}
                                className={`w-full text-left px-3 py-2.5 rounded-md border transition-colors ${selected?.id === s.id ? "border-kcc-copper bg-white/[0.05]" : "border-white/10 hover:border-white/25"}`}
                                data-testid={`storyline-${s.id}`}>
                                <div className="flex items-center gap-2">
                                    <StatusChip status={s.severity === "P1" ? "critical" : "warning"}>{s.severity}</StatusChip>
                                    <span className="text-white font-medium text-sm flex-1">{s.title}</span>
                                </div>
                                <div className="text-[11px] kcc-text-silver mt-1">{s.category} · {s.step_count} steps · ${s.impact_musd}M · {s.duration_h}h</div>
                            </button>
                        ))}
                    </div>
                </Panel>
                <div className="xl:col-span-2 space-y-4">
                    {!detail ? (
                        <Panel title="Select a storyline" accent="purple">
                            <div className="text-sm kcc-text-silver flex items-start gap-3">
                                <Clapperboard className="w-5 h-5 text-kcc-copper-light mt-0.5" />
                                <div>Choose a scenario from the library to see how KCC360 orchestrates the response across every workspace.</div>
                            </div>
                        </Panel>
                    ) : (
                        <>
                            <Panel title={detail.title} accent="copper" testid="storyline-detail">
                                <div className="text-sm kcc-text-silver leading-relaxed">{detail.summary}</div>
                                <div className="mt-3 text-[11px] kcc-text-silver">
                                    <span className="text-kcc-amber">Trigger:</span> {detail.trigger}
                                </div>
                                <div className="mt-4 flex items-center gap-2">
                                    <button onClick={() => setPlaying((p) => !p)} className="inline-flex items-center gap-2 bg-kcc-copper hover:bg-kcc-copper-light text-white rounded-md px-4 py-2 text-sm" data-testid="storyline-play">
                                        {playing ? <Pause className="w-4 h-4" /> : <Play className="w-4 h-4" />}{playing ? "Pause" : "Play"} storyline
                                    </button>
                                    <button onClick={stepBack} className="inline-flex items-center gap-1 text-xs px-3 py-2 rounded-md border border-white/10 text-kcc-silver hover:text-white" data-testid="storyline-prev"><ChevronLeft className="w-3 h-3" />Back</button>
                                    <button onClick={stepFwd} className="inline-flex items-center gap-1 text-xs px-3 py-2 rounded-md border border-white/10 text-kcc-silver hover:text-white" data-testid="storyline-next">Next<ChevronRight className="w-3 h-3" /></button>
                                    <div className="ml-auto text-[11px] kcc-text-silver">Step {step + 1} of {detail.steps.length}</div>
                                </div>
                                <div className="mt-3 h-1.5 rounded-full bg-white/5 overflow-hidden">
                                    <div className="h-full bg-kcc-copper transition-all duration-500" style={{ width: `${((step + 1) / detail.steps.length) * 100}%` }} />
                                </div>
                            </Panel>
                            <Panel title="Response across every workspace" accent="purple">
                                <div className="space-y-2" data-testid="storyline-steps">
                                    {detail.steps.map((s, i) => {
                                        const active = i === step;
                                        const past = i < step;
                                        return (
                                            <div key={s.seq} className={`p-3 rounded-md border transition-all ${active ? "border-kcc-copper bg-white/[0.05]" : past ? "border-white/10 opacity-70" : "border-white/5 opacity-45"}`}>
                                                <div className="flex items-start gap-3">
                                                    <div className={`w-7 h-7 flex items-center justify-center rounded-full text-xs font-mono-kcc ${active ? "bg-kcc-copper text-white" : past ? "bg-kcc-copper/40 text-white" : "bg-white/5 text-kcc-silver"}`}>{s.seq}</div>
                                                    <div className="flex-1">
                                                        <div className="flex items-center gap-2 flex-wrap">
                                                            <span className="text-white text-sm font-medium">{s.workspace}</span>
                                                            <span className="kcc-text-dim text-[11px]">· owner: {s.owner}</span>
                                                        </div>
                                                        <div className="text-[12px] kcc-text-silver mt-1">{s.action}</div>
                                                    </div>
                                                    {s.route && (
                                                        <button onClick={() => nav(s.route)} className="text-[11px] inline-flex items-center gap-1 px-2 py-1 rounded border border-white/10 hover:border-kcc-copper text-kcc-silver hover:text-white" data-testid={`storyline-open-${s.seq}`}>
                                                            Open <ArrowRight className="w-3 h-3" />
                                                        </button>
                                                    )}
                                                </div>
                                            </div>
                                        );
                                    })}
                                </div>
                            </Panel>
                            {/* ======================= Executive AI Situation Room ======================= */}
                            <Panel title="Executive AI Narration · COO briefing the CEO" accent="cobalt" testid="situation-narration">
                                <div className="flex items-start gap-3">
                                    <div className="w-8 h-8 rounded-full flex items-center justify-center bg-kcc-cobalt/20 border border-kcc-cobalt/40 shrink-0">
                                        <Brain className="w-4 h-4 text-kcc-cobalt-light" />
                                    </div>
                                    <div className="flex-1 min-w-0">
                                        <div className="text-[11px] kcc-text-silver flex items-center gap-2">
                                            {narrating ? (<><Loader2 className="w-3 h-3 animate-spin" /> AI narrating step {detail.steps[step]?.seq}…</>) : (<><Radio className="w-3 h-3 text-kcc-copper-light" /> Grounded on KCC360 live operational snapshot</>)}
                                        </div>
                                        <div className="mt-3 text-sm text-white whitespace-pre-wrap leading-relaxed min-h-[110px]" data-testid="narration-text">
                                            {narrations[detail.steps[step]?.seq] || <span className="kcc-text-silver">AI narration will appear here when this step is reached.</span>}
                                        </div>
                                    </div>
                                </div>
                            </Panel>
                            <Panel title="Ask the AI about this incident" accent="purple" testid="situation-ask">
                                <div className="flex items-center gap-2">
                                    <MessagesSquare className="w-4 h-4 text-kcc-purple shrink-0" />
                                    <input value={askQ} onChange={(e) => setAskQ(e.target.value)} onKeyDown={(e) => e.key === "Enter" && submitAsk()} placeholder="e.g. What is the fastest recovery option? What is the community impact?" className="flex-1 bg-black/30 border border-white/10 rounded-md px-3 py-2 text-sm text-white placeholder:text-kcc-silver/50" data-testid="ask-input" />
                                    <button onClick={submitAsk} disabled={askBusy || !askQ.trim()} className="inline-flex items-center gap-2 bg-kcc-cobalt hover:bg-kcc-cobalt-light disabled:opacity-40 text-white rounded-md px-3 py-2 text-sm" data-testid="ask-submit">
                                        {askBusy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />} Ask
                                    </button>
                                </div>
                                {askA && (
                                    <div className="mt-3 text-sm text-white whitespace-pre-wrap leading-relaxed p-3 rounded-md border border-white/10 bg-white/[0.03]" data-testid="ask-answer">{askA}</div>
                                )}
                            </Panel>
                            <Panel title="Executive Incident Report · Board Memorandum" accent="copper" testid="situation-report">
                                {!report ? (
                                    <div className="flex items-center gap-3">
                                        <button onClick={generateReport} disabled={reportBusy} className="inline-flex items-center gap-2 bg-kcc-copper hover:bg-kcc-copper-light disabled:opacity-40 text-white rounded-md px-4 py-2 text-sm" data-testid="report-generate">
                                            {reportBusy ? <Loader2 className="w-4 h-4 animate-spin" /> : <FileSignature className="w-4 h-4" />} Generate Executive Incident Report
                                        </button>
                                        <div className="text-[11px] kcc-text-silver">Auto-writes to the Executive Decision Register, watermarked, audit-signed.</div>
                                    </div>
                                ) : (
                                    <div data-testid="report-content">
                                        <div className="flex items-center flex-wrap gap-2 mb-3">
                                            <StatusChip status={report.severity === "P1" ? "critical" : "warning"}>{report.severity}</StatusChip>
                                            <span className="text-white text-sm font-medium">{report.storyline_title}</span>
                                            <span className="kcc-text-dim text-[11px] font-mono-kcc ml-auto">{report.id}</span>
                                        </div>
                                        <div className="text-[10px] kcc-text-dim tracking-widest uppercase mb-3">{report.watermark}</div>
                                        <div className="max-h-96 overflow-y-auto text-sm text-white leading-relaxed whitespace-pre-wrap p-3 rounded-md border border-white/10 bg-white/[0.02]">{report.content_md}</div>
                                        {report.decision_logged && (
                                            <div className="mt-3 text-[11px] kcc-text-silver flex items-center gap-2">
                                                <ShieldCheck className="w-3.5 h-3.5 text-kcc-copper-light" />
                                                Auto-logged to Executive Decision Register as <span className="font-mono-kcc text-white">{report.decision_logged.ref}</span> · board-level: {report.decision_logged.board_level ? "yes" : "no"}
                                            </div>
                                        )}
                                    </div>
                                )}
                                {reportErr && (<div className="mt-3 text-xs text-kcc-red">Report generation failed: {reportErr}</div>)}
                            </Panel>
                        </>
                    )}
                </div>
            </div>
        </div>
    );
}


/* ============================================================
 * Executive Presentation Mode — full-screen slide reel over the platform
 * ==========================================================*/
export function PresentationMode() {
    const [slides, setSlides] = useState([]);
    const [idx, setIdx] = useState(0);
    const [running, setRunning] = useState(false);
    const [full, setFull] = useState(false);
    const [durationSec, setDurationSec] = useState(8);
    const timerRef = useRef(null);
    const nav = useNavigate();

    // Load presentation reel once on mount.
    // eslint-disable-next-line react-hooks/exhaustive-deps
    useEffect(() => { api.get("/presentation/reel").then(({ data }) => setSlides(data.slides || [])); }, []);

    useEffect(() => {
        clearInterval(timerRef.current);
        if (running && slides.length) {
            timerRef.current = setInterval(() => {
                setIdx((i) => (i + 1) % slides.length);
            }, durationSec * 1000);
        }
        return () => clearInterval(timerRef.current);
    }, [running, slides, durationSec]);

    const current = slides[idx];

    return (
        <div className="space-y-6">
            <ModuleHeader kicker="Executive Presentation Mode"
                title="One-click Board Presentation Experience"
                description="A curated executive tour of KCC360 — auto-rotating slides across every workspace with executive narration. Launch for board rooms, investor briefings and customer demos." />
            <div className="flex flex-wrap items-center gap-3">
                <button onClick={() => { setRunning(true); setFull(true); }} className="inline-flex items-center gap-2 bg-kcc-copper hover:bg-kcc-copper-light text-white rounded-md px-5 py-2.5 text-sm" data-testid="present-launch">
                    <Maximize2 className="w-4 h-4" /> Launch presentation
                </button>
                <button onClick={() => setRunning((r) => !r)} className="inline-flex items-center gap-2 text-sm px-4 py-2.5 rounded-md border border-white/10 hover:border-white/25 text-kcc-silver hover:text-white" data-testid="present-toggle">
                    {running ? <Pause className="w-4 h-4" /> : <Play className="w-4 h-4" />}{running ? "Pause auto-rotate" : "Start auto-rotate"}
                </button>
                <label className="text-xs kcc-text-silver flex items-center gap-2">Slide duration
                    <select value={durationSec} onChange={(e) => setDurationSec(parseInt(e.target.value))} className="bg-black/30 border border-white/10 rounded px-2 py-1 text-xs text-white" data-testid="present-duration">
                        <option value="5">5s</option>
                        <option value="8">8s</option>
                        <option value="12">12s</option>
                        <option value="20">20s</option>
                    </select>
                </label>
                <div className="ml-auto text-[11px] kcc-text-silver">{slides.length} slides · {(slides.length * durationSec / 60).toFixed(1)} min tour</div>
            </div>

            <div className="grid grid-cols-1 xl:grid-cols-4 gap-4">
                {slides.map((s, i) => (
                    <button key={s.seq} onClick={() => setIdx(i)}
                        className={`text-left p-4 rounded-md border transition-colors ${i === idx ? "border-kcc-copper bg-white/[0.05]" : "border-white/10 hover:border-white/25"}`}
                        data-testid={`slide-${s.seq}`}>
                        <div className="kcc-label text-kcc-copper-light">{s.kicker}</div>
                        <div className="font-display text-lg font-semibold text-white mt-1 leading-tight">{s.title}</div>
                        <div className="text-[11px] kcc-text-silver mt-2 line-clamp-2">{s.narration}</div>
                        <div className="text-[10px] kcc-text-dim mt-2 font-mono-kcc">{s.route}</div>
                    </button>
                ))}
            </div>

            {full && current && (
                <div className="fixed inset-0 z-[100] bg-black" data-testid="present-fullscreen">
                    <div className="absolute inset-0" style={{ background: "radial-gradient(circle at 30% 20%, rgba(184,115,51,0.16), transparent 55%), radial-gradient(circle at 80% 80%, rgba(45,104,196,0.20), transparent 55%), #0b0e14" }} />
                    <div className="relative z-10 h-full flex flex-col">
                        <div className="p-6 flex items-center justify-between">
                            <div className="flex items-center gap-3">
                                <div className="w-10 h-10 rounded-md flex items-center justify-center" style={{ background: "linear-gradient(135deg,#b87333,#2d68c4)" }}>
                                    <span className="font-display font-bold text-white text-lg">K</span>
                                </div>
                                <div>
                                    <div className="font-display text-xl text-white leading-none">KCC<span className="text-kcc-copper-light">360</span></div>
                                    <div className="text-[10px] kcc-text-silver mt-1">KAMOTO COPPER COMPANY · EXECUTIVE PRESENTATION</div>
                                </div>
                            </div>
                            <div className="flex items-center gap-2">
                                <button onClick={() => setRunning((r) => !r)} className="text-white/70 hover:text-white text-xs inline-flex items-center gap-1 px-3 py-1.5 rounded-md border border-white/10">{running ? <><Pause className="w-3 h-3" />Pause</> : <><Play className="w-3 h-3" />Play</>}</button>
                                <button onClick={() => { setFull(false); setRunning(false); }} className="text-white/70 hover:text-white text-xs inline-flex items-center gap-1 px-3 py-1.5 rounded-md border border-white/10" data-testid="present-exit"><X className="w-3 h-3" />Exit</button>
                            </div>
                        </div>
                        <div className="flex-1 flex items-center justify-center px-10">
                            <div className="max-w-5xl w-full" data-testid="present-slide">
                                <div className="kcc-label text-kcc-copper-light">{current.kicker}</div>
                                <div className="font-display text-6xl md:text-7xl font-semibold text-white tracking-tight mt-3 leading-[1.02]">{current.title}</div>
                                <div className="text-lg md:text-xl text-kcc-silver mt-6 max-w-3xl leading-relaxed">{current.narration}</div>
                                <div className="mt-10 flex items-center gap-3">
                                    <button onClick={() => { setFull(false); setRunning(false); nav(current.route); }} className="inline-flex items-center gap-2 bg-kcc-copper hover:bg-kcc-copper-light text-white rounded-md px-5 py-2.5 text-sm">
                                        Open workspace <ArrowRight className="w-4 h-4" />
                                    </button>
                                    <div className="text-xs kcc-text-silver font-mono-kcc">{current.route}</div>
                                </div>
                            </div>
                        </div>
                        <div className="p-6 flex items-center gap-4">
                            <button onClick={() => setIdx((i) => (i - 1 + slides.length) % slides.length)} className="text-white/70 hover:text-white text-xs inline-flex items-center gap-1 px-3 py-1.5 rounded-md border border-white/10"><ChevronLeft className="w-3 h-3" />Prev</button>
                            <div className="flex-1 flex items-center gap-1.5">
                                {slides.map((s, i) => (
                                    <div key={s.seq} onClick={() => setIdx(i)} className={`flex-1 h-1 rounded-full cursor-pointer ${i === idx ? "bg-kcc-copper" : i < idx ? "bg-kcc-copper/40" : "bg-white/10"}`} />
                                ))}
                            </div>
                            <div className="text-[11px] kcc-text-silver font-mono-kcc">{idx + 1}/{slides.length}</div>
                            <button onClick={() => setIdx((i) => (i + 1) % slides.length)} className="text-white/70 hover:text-white text-xs inline-flex items-center gap-1 px-3 py-1.5 rounded-md border border-white/10">Next<ChevronRight className="w-3 h-3" /></button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
}
